<template>
    <div style="overflow-x: hidden" v-if="wizardData" fluid>
        <b-row class="mt-3 pl-3">
            <b-col>
                <b>{{ wizardData.name }} - Room Types</b>
            </b-col>
        </b-row>
        <hr />
        <grid
            ref="grid"
            :row-data="rowData"
            :column-defs="columnDefs"
            :header-height="45"
            :is-search="false"
            :is-auto-height="true"
            :is-auto-width="true"
            :enableCellTextSelection="false"
            :saveRowOrder="saveRowOrder"
        >
        </grid>
        <b-row class="my-3 pl-2">
            <b-col>
                <responsive-button
                    variant="primary"
                    :responsiveness="false"
                    :href="`/app/property/property/${propertyId}/create-roomtype`"
                    >Create Room Type</responsive-button
                >
            </b-col>
        </b-row>
    </div>
</template>

<script>
import { mapGetters } from 'vuex';
import API from '../../../../api';
import Grid from '../../../generic/grid/grid.vue';
import Modal from '../../../generic/modal/modal.vue';
import FormInput from '../../../generic/form-input/form-input.vue';
import ResponsiveButton from '../../../generic/responsive-button/responsive-button.vue';
import ActionsCellRenderer from './actions-cell-renderer.vue';
import StatusCellRenderer from './status-cell-renderer.vue';
import { formatResponseError } from '../../../../utils/api';
import { removeScrollMixin } from '../../../../mixins/remove-scroll-mixin';
import { RENTAL_PROPERTY_SETUP_ROOMTYPES_ENDPOINT } from '../../../../endpoints';

export default {
    name: 'RoomTypes',
    components: {
        Grid,
        Modal,
        FormInput,
        ResponsiveButton,
        actionsCellRenderer: ActionsCellRenderer,
        statusCellRenderer: StatusCellRenderer,
    },
    mixins: [removeScrollMixin],
    inject: ['propertyId'],
    computed: {
        ...mapGetters('setupPropertyStore', ['wizardData', 'roomTypesData']),
        columnDefs() {
            return [
                {
                    field: 'order',
                    headerName: 'Order',
                    cellClass: 'first-drag-column',
                    valueGetter: ({ data }) =>
                        data.order >= 999999 ? 'N/A' : data.order,
                    rowDrag: ({ data }) => data.order < 999999,
                    rowDragText: ({ defaultTextValue, rowNode }) =>
                        `${defaultTextValue} ${rowNode.data.name}`,
                },
                {
                    field: 'id',
                    headerName: 'ID',
                    rowDrag: ({ data }) =>
                        data.order < 999999 && this.isMediumScreen,
                    rowDragText: ({ rowNode }) =>
                        `${rowNode.data.order} ${rowNode.data.name}`,
                },
                {
                    field: 'name',
                    headerName: 'Name',
                    autoWidthExclude: true,
                    rowDrag: ({ data }) =>
                        data.order < 999999 && this.isMediumScreen,
                    rowDragText: ({ defaultTextValue, rowNode }) =>
                        `${rowNode.data.order} ${defaultTextValue}`,
                },
                {
                    field: 'property_type',
                    headerName: 'Type',
                    autoWidthExclude: true,
                    rowDrag: ({ data }) =>
                        data.order < 999999 && this.isMediumScreen,
                    rowDragText: ({ defaultTextValue, rowNode }) =>
                        `${defaultTextValue} ${rowNode.data.name}`,
                },
                {
                    field: 'status',
                    headerName: 'Status',
                    autoWidthExclude: true,
                    valueGetter: ({ data }) => !data.disabled,
                    cellRenderer: 'statusCellRenderer',
                    cellClass: 'd-flex justify-content-center',
                    headerClass: 'ag-centered-header',
                    cellRendererParams: {
                        onChange: async ({ data, node, api }, value) => {
                            try {
                                await API.post(
                                    `${RENTAL_PROPERTY_SETUP_ROOMTYPES_ENDPOINT(
                                        this.propertyId
                                    )}enable-disable/`,
                                    { roomtype_id: data.id, enable: value }
                                );
                                this.refetchRoomtypes();
                            } catch (e) {
                                const { generic } = formatResponseError(e);
                                this.errorToast({
                                    text: generic || 'Could not save',
                                });
                                api.redrawRows({ rowNodes: [node] });
                                return;
                            }
                        },
                    },
                    rowDrag: ({ data }) =>
                        data.order < 999999 && this.isMediumScreen,
                    rowDragText: ({ defaultTextValue, rowNode }) =>
                        `${defaultTextValue} ${rowNode.data.name}`,
                },
                {
                    field: 'qty',
                    headerName: 'Units',
                    autoWidthExclude: true,
                    rowDrag: ({ data }) =>
                        data.order < 999999 && this.isMediumScreen,
                    rowDragText: ({ defaultTextValue, rowNode }) =>
                        `${defaultTextValue} ${rowNode.data.name}`,
                    resizable: false,
                    minWidth: 125,
                },
                {
                    field: '',
                    maxWidth: 130,
                    minWidth: 130,
                    resizable: false,
                    sortable: false,
                    cellRenderer: 'actionsCellRenderer',
                    cellRendererParams: {
                        onDelete: async (id, modalId) => {
                            try {
                                await API.post(
                                    `${RENTAL_PROPERTY_SETUP_ROOMTYPES_ENDPOINT(
                                        this.propertyId
                                    )}delete-roomtype/`,
                                    { roomtype_id: id }
                                );
                                this.$bvModal.hide(modalId);
                                this.refetchRoomtypes();
                            } catch (e) {
                                const { generic } = formatResponseError(e);
                                this.errorToast({
                                    text:
                                        generic ||
                                        'Could not delete room type.',
                                });
                            }
                        },
                    },
                },
            ];
        },
        isMediumScreen() {
            const md = 1024;
            return window.innerWidth >= md;
        },
    },
    data() {
        return {
            busy: false,
            rowData: [],
        };
    },
    async mounted() {
        await this.fetchRoomtypes();
    },
    methods: {
        async fetchRoomtypes() {
            try {
                this.$parent.$emit('busy', true);
                if (!this.roomTypesData) {
                    const data = await this.$store.dispatch(
                        'setupPropertyStore/getRoomTypesData',
                        { propertyId: this.propertyId }
                    );
                    this.rowData = data;
                } else {
                    this.rowData = this.roomTypesData;
                }
            } catch (e) {
                this.errorToast({ text: 'Could not load room types.' });
            } finally {
                this.$parent.$emit('busy', false);
            }
        },
        async refetchRoomtypes() {
            try {
                this.$parent.$emit('busy', true);
                const data = await this.$store.dispatch(
                    'setupPropertyStore/getRoomTypesData',
                    { propertyId: this.propertyId }
                );
                this.rowData = data;
            } catch (e) {
                this.errorToast({ text: 'Could not load room types.' });
            } finally {
                this.$parent.$emit('busy', false);
            }
        },
        async saveRowOrder({ row, order, onSuccess, onError }) {
            try {
                if (row.order < 999999) {
                    this.$parent.$emit('busy', true);
                    await API.post(
                        `${RENTAL_PROPERTY_SETUP_ROOMTYPES_ENDPOINT(
                            this.propertyId
                        )}set-order/`,
                        { roomtype_id: row.id, order: order }
                    );
                    this.refetchRoomtypes();
                }
            } catch (e) {
                const { generic } = formatResponseError(e);
                this.errorToast({
                    text: generic || 'Could not reorder room types.',
                });
            } finally {
                this.$parent.$emit('busy', false);
            }
        },
    },
};
</script>

<style lang="scss" scoped>
@import '~bootstrap/scss/_functions.scss';
@import '~bootstrap/scss/_variables.scss';
@import '~bootstrap/scss/mixins/_breakpoints.scss';

:deep(.ag-icon-grip) {
    position: absolute;
    pointer-events: auto;
    top: 0;
    opacity: 0;
    width: 100%;
    height: 100%;
}
:deep(.ag-drag-handle.ag-row-drag) {
    margin-right: 0px;
}
:deep(.first-drag-column .ag-drag-handle.ag-row-drag) {
    margin-right: 12px;
}
:deep(.first-drag-column .ag-icon-grip) {
    position: relative;
    top: 12px;
    opacity: 1;
}
</style>
